/**
 * Members that must agree on a shared left/right width (a “gutter”).
 *
 * Each member publishes how much space it needs; the group uses the widest need on each side,
 * rounded up to the next 4px. API is feature-agnostic — data-viewer stacked plot panels are the
 * first consumer (tick/badge gutters), not the only intended one.
 *
 * Invariant: a member’s *own* gutter must not depend on the *shared* gutter, or members widen
 * each other in a loop.
 */

type SideGuttersPx = { left: number; right: number };
/** How much left/right space this member needs. */
export type OwnGutter = SideGuttersPx;
/** Stack-wide left/right gutter after max + quantize. */
export type SharedGutter = SideGuttersPx;

/** Called with the group's new shared gutter whenever it changes. */
type SharedGutterListener = (sharedGutter: SharedGutter) => void;

/** Quantization step, in pixels. Own gutters round UP to the next multiple of this. */
const QUANTIZE_STEP_PX = 4;

/** One shared {0,0} for empty groups so React doesn't think the gutter changed every render. */
export const EMPTY_SHARED_GUTTER: SharedGutter = Object.freeze({ left: 0, right: 0 });

/** Round a single side up to the next quantization step. 0 stays 0. */
function quantizeUp(value: number): number {
    if (value <= 0) return 0;
    return Math.ceil(value / QUANTIZE_STEP_PX) * QUANTIZE_STEP_PX;
}

/** Widest own gutter on each side, rounded up to the next quantized step. */
export function widestGutter(ownGutters: OwnGutter[]): SharedGutter {
    const left = ownGutters.reduce((max, gutter) => Math.max(max, gutter.left), 0);
    const right = ownGutters.reduce((max, gutter) => Math.max(max, gutter.right), 0);
    return { left: quantizeUp(left), right: quantizeUp(right) };
}

export interface WidthGroupRegistry {
    setOwnGutter(groupId: string, memberId: string, ownGutter: OwnGutter): void;
    unregister(groupId: string, memberId: string): void;
    getSharedGutter(groupId: string): SharedGutter;
    subscribe(groupId: string, listener: SharedGutterListener): () => void;
}

type GroupState = {
    ownGutters: Map<string, OwnGutter>;
    subscribers: Set<SharedGutterListener>;
    /** Cached stack-wide gutter; same object reference until left or right actually changes. */
    cachedSharedGutter: SharedGutter;
};

export function createWidthGroupRegistry(): WidthGroupRegistry {
    const groups = new Map<string, GroupState>();

    function getOrCreateGroup(groupId: string): GroupState {
        let group = groups.get(groupId);
        if (!group) {
            group = {
                ownGutters: new Map(),
                subscribers: new Set(),
                cachedSharedGutter: EMPTY_SHARED_GUTTER,
            };
            groups.set(groupId, group);
        }
        return group;
    }

    /** Recompute the shared gutter and notify subscribers only when left or right changed. */
    function recomputeAndNotify(group: GroupState): void {
        const resolved = widestGutter([...group.ownGutters.values()]);
        const next = resolved.left === 0 && resolved.right === 0 ? EMPTY_SHARED_GUTTER : resolved;
        const prev = group.cachedSharedGutter;
        if (next.left === prev.left && next.right === prev.right) {
            return;
        }
        group.cachedSharedGutter = next;
        // Hand listeners the value we just cached, so a listener and getSharedGutter can never disagree.
        group.subscribers.forEach((listener) => {
            listener(next);
        });
    }

    return {
        setOwnGutter(groupId, memberId, ownGutter) {
            const group = getOrCreateGroup(groupId);
            group.ownGutters.set(memberId, { left: ownGutter.left, right: ownGutter.right });
            recomputeAndNotify(group);
        },

        unregister(groupId, memberId) {
            const group = groups.get(groupId);
            if (!group) return;
            const existed = group.ownGutters.delete(memberId);
            if (!existed) return;
            recomputeAndNotify(group);
        },

        getSharedGutter(groupId) {
            const group = groups.get(groupId);
            return group ? group.cachedSharedGutter : EMPTY_SHARED_GUTTER;
        },

        subscribe(groupId, listener) {
            const group = getOrCreateGroup(groupId);
            group.subscribers.add(listener);
            return () => {
                group.subscribers.delete(listener);
            };
        },
    };
}
